<template>
    <div class="teamInt">
        <div v-if="!dataLoaded" class="w-full flex justify-center p-2">
            <c-loader color="rgba(var(--vs-primary), 1)"></c-loader>
        </div>

        <ds-table
            v-else
            ref="table"
            :max-items="integrations.length"
            :data="integrations"
            :heads="headsGroup"
            :noDataText="i('table.No_data_available')"
            :paginate="false"
            spacer
        >
            <template v-slot:name="{name}">
                <p class="product-name font-medium truncate">{{`${name.advertiser_name || name.advertiser_id} / ${name.advertiser_integration_name}`}}</p>
            </template>

            <template v-slot:geo="{geo}">
                <p class="product-name font-medium truncate">{{geo.geo_name}}</p>
            </template>

            <template v-slot:model="{model}">
                <p class="product-name font-medium truncate">{{ model.traffic_model_name }}</p>
            </template>


            <template v-slot:priority="{priority}">
                <div class="flex justify-center">
                    <v-status
                        v-tooltip.top="$t(setStatusTooltip(priority))"
                        :color-by-vs="setStatusColor(priority)"
                    />
                </div>
            </template>

            <template v-slot:countries="{countries}">
                <vs-chip class="inline-block float-none" v-tooltip.left="countries.countries_list.join('<br>')">
                    <vs-avatar text-color="#175ea5" color="#f0f0f0" v-bind="$iconData('icon-globe', 'fi-rr-globe')"/>
                    <span>{{ countries.countriesListLength }}</span>
                </vs-chip>
            </template>

            <template v-slot:schedule="{schedule}">
                <div class="flex schedule">
                    <vs-chip class="days-chip schedule text-center rounded-lg textchip-col" v-for="(day, ind) in schedule.scheduling_caps" :key="ind">
                        <div class="days-btn p-1 rounded-lg cursor-pointer items-center justify-center font-bold" :class="day.is_active ? 'active' : ''">
                            {{ i(`calendar.daysOfWeek_short.${dayOfWeek[ind]}`)}}
                        </div>
                        <div class="days-time">{{ day.is_active ? day.cc_work_start+' : '+day.cc_work_end : 'N/A' }}</div>
                    </vs-chip>
                </div>
            </template>

            <template v-slot:offers="{offers}">
                <vs-chip class="inline-block float-none"
                         v-tooltip.left="offers.offers_list.join('<br>')">
                    <vs-avatar text-color="#175ea5" color="#f0f0f0" v-bind="$iconData('icon-briefcase', 'fi-rr-briefcase')"/>
                    <span>{{ offers.offersListLength }}</span>
                </vs-chip>
            </template>

            <template v-slot:active="{active}">
                <vs-switch
                    v-can="'settings_team.pause_integration'"
                    color="success"
                    v-model="active.is_active"
                    @click.prevent.stop="changeTeamIntStatus($event, active)"
                    icon-pack="feather"
                    vs-icon-off="icon-x"
                    vs-icon-on="icon-check"
                />
                <vs-switch v-can:show="'settings_team.pause_integration'"
                           color="success"
                           v-model="active.is_active"
                           disabled
                           icon-pack="feather"
                           vs-icon-off="icon-x"
                           vs-icon-on="icon-check"
                />
            </template>

            <template v-slot:action="{action}">
                <div class="flex items-center h-full">
                    <vs-button v-can="'settings_team.update_integration'" radius color="primary" type="flat"
                               @click="editTeamIntegration(action.id, action)"
                               v-tooltip="i('modals.Edit_integration')"
                               :disabled="loading"
                               v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')"
                    ></vs-button>
                    <vs-button radius color="primary" type="flat"
                               v-bind="$iconData('icon-clock', 'fi-rr-clock-three')"
                               @click="updateTime(action.id, action)"
                               v-tooltip="i('modals.editSchedule')"
                               :disabled="loading"
                    ></vs-button>
                    <vs-button v-can:show="'settings_team.update_integration'" radius color="primary" type="flat"
                               v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')" disabled></vs-button>
                    <vs-button v-can="'settings_team.destroy_integration'" radius color="primary"
                               type="flat"
                               v-bind="$iconData('icon-trash-2', 'fi-rr-trash')"
                               @click="removeTeamIntegration($event, action)"
                               v-tooltip="i('tooltip.Delete_integration')" :disabled="loading"></vs-button>
                    <vs-button v-can:show="'settings_team.destroy_integration'" radius color="primary"
                               type="flat" v-bind="$iconData('icon-trash-2', 'fi-rr-trash')" disabled></vs-button>
                </div>
            </template>
        </ds-table>
    </div>
</template>

<script>
import store from '../../../store/store'
import EventBus from '../../../store/eventBus'
import i18n from '../../../i18n/i18n'
import ErrorHandler from '../../../utils/errorHandler'
import {sorting} from '@/utils/mixins'
import send from "../../../utils/fetch";
import VStatus from "@/components/Status.vue";

export default {
    components: {VStatus},
    props: {
        teamId: Number,
        groupedTeams: {
            type: Boolean,
            default: false
        },
        details: Array,
    },
    mixins: [sorting],
    data() {
        return {
            loading: false,
            dayOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            headsGroup: [
                {name: this.groupedTeams ? 'global.Integration' : 'statistic.ADV_INT', key: 'name', sortKey: this.groupedTeams ? 'name' : 'advertiser_name', value: 'name', display: true},
                {name: 'global.Geo', key: 'geo', sortKey: 'geo_name', value: 'geo', headerPosition: 'center', display: true},
                {name: 'global.model', key: 'model', sortKey: 'traffic_model_name', headerPosition: 'center', value: 'model', display: true},
                {name: 'global.Countries', key: 'countries', sortKey: 'countriesListLength', headerPosition: 'center', value: 'countries', display: true},
                {name: 'global.Offers', key: 'offers', sortKey: 'offersListLength', headerPosition: 'center', value: 'offers', display: true},
                {name: 'global.Priority', key: 'priority', sortKey: 'is_low_priority', headerPosition: 'center', value: 'priority', width: '120px', center: true, display: true},
                {name: 'global.Schedule', key: 'schedule', value: 'schedule', center: true, headerPosition: 'center', width: '130px', display: true},
                {name: 'global.Status', key: 'status', sortKey: 'is_active', value: 'active', headerPosition: 'center', width: '80px', display: true},
                {name: 'global.Actions', key: 'action', value: 'action', headerPosition: 'end', width: '130px', display: true}
            ],
            headsNotGroup: [],
            isMounted: false,
            loadingEl: null,
            currentTrueIndex: null,
            currentArrIndex: null,
            dataLoaded: false,
            teamIntegrationsData: null,
            integrations: []
        }
    },
    methods: {
        setStatusColor(data) {
            const priorityMap = {
                'none': 'success',
                'all': 'danger',
                'part': 'warning'
            };
            return priorityMap[data.is_low_priority] || 'all';
        },

        setStatusTooltip(data) {
            const priorityMap = {
                'none': 'tooltip.statusNone',
                'all': 'tooltip.statusAll',
                'part': 'tooltip.statusPart'
            };
            return priorityMap[data.is_low_priority] || 'all';
        },

        updateTime(id, data) {
            data = {...data, team_id: this.teamId, is: 'teams'}
            EventBus.$emit('emitPopupEvent', {type: 'editIntegrationTeamTime', name: this.$t('modals.editSchedule'), data})
        },

        i(val) {
            return i18n.tc(val)
        },

        editTeamIntegration(arrId, data) {
            this.loading = true
            EventBus.$emit('emitPopupEvent', {
                type: this.groupedTeams ? 'editTeamIntegrationWhenGroupedTeam' : 'editTeamIntegration',
                name: this.i('modals.Edit_integration'),
                ids: {
                    currentTeamId: this.teamId,
                    teamIntArrId: arrId,
                    data,
                    groupedTeams: this.groupedTeams,
                    users: this.details
                },
                full: true
            })
        },

        removeTeamIntegration(e, data) {
            this.loadingEl = e.target
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: this.i('confirm.Confirm_action'),
                text: this.i('confirm.deleteIntegration'),
                accept: async () => {
                    try {
                        this.$vs.loading({
                            background: 'rgba(255,255,255,0.75)',
                            color: 'primary',
                            container: this.loadingEl,
                            scale: +'0.45'
                        })
                        const dispatchPath = 'destroy',
                            dispatchData = {
                                url: this.$url.advrtsr_team_integration.destroy,
                                mutation: 'REMOVE_TEAM_INTEGRATIONS',
                                data: {
                                    advertiser_integration_id: data.advertiser_integration_id,
                                    team_id: this.teamId,
                                }
                            }

                        await store.dispatch(dispatchPath, dispatchData)

                        this.$vs.loading.close(this.loadingEl)
                        this.loadingEl = this.currentTrueIndex = null

                        const dispatchPathSet = 'fetchId', dispatchDataSet = {
                            url: this.$url.advrtsr_team_integration.get,
                            mutation: 'FETCH_TEAM_INTEGRATIONS',
                            id: this.teamId
                        }
                        const dispatchPathTeam = 'fetch', dispatchDataTeam = {
                            url: this.$url.settings_team.get,
                            mutation: 'SET_TEAMS'
                        }
                        // Dispatch
                        await store.dispatch(dispatchPathTeam, dispatchDataTeam)
                        await store.dispatch(dispatchPathSet, dispatchDataSet)

                        EventBus.$emit('getTeamsInt')
                    } catch(e) {
                        this.$vs.loading.close(this.loadingEl)
                        this.loadingEl = this.currentTrueIndex = null
                        new ErrorHandler(e).send
                    }
                },
                acceptText: this.i('buttons.Delete'),
                cancelText: this.i('global.Cancel')
            })
        },

        changeTeamIntStatus(e, data) {
            this.loadingEl = e.target
            this.loadingEl = e.target.parentNode
            this.$vs.dialog({
                type: 'confirm',
                color: 'warning',
                title: this.$t('confirm.Confirm_action'),
                text: this.$t(!data.is_active ? 'confirm.activateIntegration' : 'confirm.deactivateIntegration'),
                accept: async () => {
                    try {
                        await send(this.$url.advrtsr_team_integration.pause, 'PUT', {
                            data: {
                                advertiser_integration_id: data.advertiser_integration_id,
                                team_id: this.teamId,
                                is_active: !data.is_active
                            }
                        })

                        EventBus.$emit('getTeamsInt')
                    } catch (e) {
                        new ErrorHandler(e).send
                    }
                },
                acceptText: this.$t(data.is_active ? 'buttons.Deactivate' : 'buttons.Activate'),
                cancelText: this.$t('global.Cancel')
            })
        },

        isLowPriority(deals = []) {
            let lowCount = 0;
            let hideCount = 0;

            for (const deal of deals) {
                if (deal.is_low_priority) {
                    lowCount += 1;
                } else {
                    hideCount += 1;
                }
            }

            if (hideCount > 0 && lowCount === 0) {
                return 'none';
            } else if (lowCount > 0 && hideCount === 0) {
                return 'all';
            } else {
                return 'part';
            }
        },


        async get() {
            try {
                const response = await send(this.$url.advrtsr_team_integration.get, 'GET', {
                    params: {id: this.teamId}
                }).then(response => response.data.result)

                this.integrations = response.map(item => {
                    const countries = new Set()
                    const offers = new Set()

                    item.deals.map(item => countries.add(item.country))
                    item.deals.map(item => offers.add(item.offer_name))

                    item.countries_list = Array.from(countries)
                    item.offers_list = Array.from(offers)

                    item.is_low_priority = this.isLowPriority(item.deals)

                    item.countriesListLength = item.countries_list.length
                    item.offersListLength = item.offers_list.length

                    return item
                })
            } catch (e) {
                new ErrorHandler(e).send
            } finally {
                this.dataLoaded = true
            }
        }
    },

    destroyed() {
        EventBus.$off(`resetInt_${this.teamId}`)
        EventBus.$off('getTeamsInt')
        EventBus.$off('close')
    },

    async mounted() {
        EventBus.$on('close', () => {
            this.loading = false
        })

        EventBus.$on('getTeamsInt', async () => {
            EventBus.$emit(`resetInt_${this.teamId}`)
        })

        EventBus.$on(`resetInt_${this.teamId}`, async () => {
            this.loading = true

            await this.get()

            this.loading = false
        })

        await this.get()
    }
}
</script>

<style lang="scss">
.teamInt {
    .schedule {
        .textchip-col.days-chip {
            min-width: 70px;
            padding-right: 0;
        }
        .textchip-col .text-chip {
            flex-direction: column!important;
            margin-left: 0!important;
        }
        td {
            padding:  10px 5px!important;
        }
        .testButton {
            .con-vs-loading {
                position: absolute !important;
            }
        }

        .days-btn {
            /*border-color: #dcdcdc;*/
            width: 60px;
            margin: 5px auto;
            background: #C3C3C3;
            color: #fff;
            /*font-size: 0.5rem;*/
            font-size: 0.55rem !important;
            padding: 0.4rem 0.3rem !important;
            text-align: center;
            &.active {
                background: #28C76F;
                color: #fff;
            }
            &.active2 {
                background: #DFF7EA;
                color: #28C76F;
            }
        }
        .days-time {
            font-size: 0.6rem;
            margin-bottom: 5px;
        }
        .con-vs-chip {
            margin-bottom: 0;
        }
    }
}

</style>
